// Angular Imports
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';

import { PartnerStateService } from '../../modules/partner/services/partner-state.service';
import { Partner } from '../../modules/partner/models/partner';
import { LoginStateService } from '../../modules/login/services/login-state.service';
import { PartnerStateEnum } from '../../modules/partner/services/partner-state.enum';
import { environment } from '../../../environments/environment';
import { LocalizationService } from '../../services/localization.service';
import { TranslateService } from '@ngx-translate/core';
import { ShopSettingsService } from '../../modules/general/services/shop-settings.service';
import { ScheduleStateService } from '../../modules/schedule/services/schedule-state.service';
import { MatDialog } from '@angular/material/dialog';
import { LanguageSelectionPopUpComponent } from '../languages/language-selection-popup/language-selection-popup.component';
import { BookingOrderService } from '../../modules/booking/services/booking-order.service';
import { switchMap, tap } from 'rxjs/operators';
import { Observable, Subscription } from 'rxjs';
import { AppStateService } from '../../services/app-state.service';
import { ThemeService } from '../../modules/general/services/theme.service';
import { RefundCardStateService } from '../../modules/refund-card/services/refund-card-state.service';

@Component({
  selector: 'app-page-header',
  templateUrl: 'page-header.component.html',
  styleUrls: ['page-header.component.css']
})
export class PageHeaderComponent implements OnInit, OnDestroy {

  mobileMenuCheckbox: boolean;
  showPartnerMenu: boolean;
  private logoutSubscription: Subscription;

  constructor(
    private router: Router,
    private partnerStateService: PartnerStateService,
    private localizationService: LocalizationService,
    private translateService: TranslateService,
    private shopSettingsService: ShopSettingsService,
    private loginStateService: LoginStateService,
    private scheduleStateService: ScheduleStateService,
    public dialog: MatDialog,
    private bookingOrderService: BookingOrderService,
    private appStateService: AppStateService,
    private themeService: ThemeService,
    private refundCardStateService: RefundCardStateService,
  ) {
    this.mobileMenuCheckbox = false;
    this.showPartnerMenu = false;
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
    this.unsubscribeLogoutSubscription();
  }

  changeMobileMenuCheckbox(): void {
    if (this.mobileMenuCheckbox === true) {
      this.mobileMenuCheckbox = false;
    } else {
      this.mobileMenuCheckbox = true;
    }
  }

  login(): void {
    this.router.navigateByUrl('login');
  }

  mobileLogin(): void {
    this.login();
    this.changeMobileMenuCheckbox();
  }

  registration(): void {
    this.router.navigateByUrl('/registration');
  }

  mobileRegistration(): void {
    this.registration();
    this.changeMobileMenuCheckbox();
  }

  mobileImpressum(): void {
    this.impressum();
    this.changeMobileMenuCheckbox();
  }

  mobileContact(): void {
    this.contact();
    this.changeMobileMenuCheckbox();
  }

  home(): void {
    if (this.cardRefundActive) {
      return;
    }
    this.router.navigateByUrl('');
    this.hidePartnerMenu();
  }

  mobileHome(): void {
    this.home();
    this.changeMobileMenuCheckbox();
  }

  booking(): void {
    this.router.navigateByUrl('booking');
  }

  giftcard(): void {
    this.router.navigateByUrl('giftcard');
  }

  mobileShowPartner(): void {
    this.showPartner();
    this.changeMobileMenuCheckbox();
  }

  showPartner(): void {
    this.router.navigateByUrl('partner');
    this.hidePartnerMenu();
  }

  contact(): void {
    if (this.contactLink) {
      return;
    }
    this.router.navigateByUrl('contact');
  }

  ticketInformation(): void {
    window.location.href = `${this.shopSettingsService.vuBaseUrl}/store/external-ticket?lang=${this.translateService.currentLang}`;
  }

  faq(): void {
    this.router.navigateByUrl('faq');
  }

  privacy(): void {
    this.router.navigateByUrl('privacy');
  }

  agb(): void {
    if (environment.globalSettings.shopCountry === 'HU') {
      this.router.navigateByUrl('aszf');
      return;
    }
    this.router.navigateByUrl('agb');
  }

  impressum(): void {
    this.router.navigateByUrl('impressum');
  }

  get partner(): Partner {
    return this.partnerStateService.loggedInPartner;
  }

  goToMyChildrenClick(): void {
    this.partnerStateService.update(PartnerStateEnum.ShowChildrenList);
    this.changeMobileMenuCheckbox();
  }

  goToBookingOverviewClick(): void {
    this.partnerStateService.update(PartnerStateEnum.ShowOrders);
    this.changeMobileMenuCheckbox();
  }

  get availableLangs(): string[]  {
    return this.localizationService.availableLangs;
  }

  getFlagFilePath(small: boolean): string {
    if (this.availableLangs.includes(this.currentLang)) {
      return small ? `${this.themeService.baseUrl}/images/flags/${this.currentLang}.png` : `${this.themeService.themeBaseUrl}/images/lang_button_${this.currentLang}.png`;
    }

    return '';
  }

  get currentLang(): string {
    return this.translateService.currentLang;
  }

  get langClassName(): string {
    return `lang_${this.currentLang}`;
  }

  public changeLang(): void {
    const currentLangIndex = this.availableLangs.indexOf(this.currentLang);

    if (currentLangIndex === -1 || this.availableLangs.length === 1) {
      return;
    }
    if (this.availableLangs.length > 2) {
      const dialogRef = this.dialog.open(
        LanguageSelectionPopUpComponent,
        {
          height: '70%',
        });
      return;
    }

    const nextLangIndex = (currentLangIndex + 1) < this.availableLangs.length ? currentLangIndex + 1 : 0;

    this.localizationService.setLanguage(this.availableLangs[nextLangIndex]);
  }

  get isVUMode(): boolean {
    return this.shopSettingsService.isVUMode;
  }

  get showVUUserMenu(): boolean {
    return this.shopSettingsService.shopSettings.showVUUserMenu;
  }

  get showHome(): boolean {
    return !this.isVUMode || !this.showVUUserMenu;
  }

  get showScheduleMenu(): boolean {
    return this.shopSettingsService.shopSettings.showScheduleMenu;
  }

  get showPartnerInformation(): boolean {
    return this.partner && this.isVUMode && this.showVUUserMenu && !this.appStateService.initialize;
  }

  get showContact(): boolean {
    return !this.isVUMode && this.shopSettingsService.shopSettings.showContactMenu;
  }

  get contactLink(): string {
    return this.shopSettingsService?.shopSettings?.contactLink || null;
  }

  get showTicketInformation(): boolean {
    return this.isVUMode && !this.showVUUserMenu && this.shopSettingsService.shopSettings.showTicketInformation;
  }

  get allowedRegistration(): boolean {
    return this.shopSettingsService.allowedRegistration;
  }

  get showCardTransactions(): boolean {
    return this.shopSettingsService.shopSettings && this.shopSettingsService.shopSettings.partnerCodePayment;
  }

  get partnerAmountBalanceText(): string {
    return this.partner && this.partner.accountBalance != null
      ? ` (${environment.globalSettings.toMoneyStr(this.partner.accountBalance)})` : '';
  }

  unsubscribeLogoutSubscription(): void {
    if (this.logoutSubscription) {
      this.logoutSubscription.unsubscribe();
      this.logoutSubscription = null;
    }
  }

  mobileLogout(): void {
    this.unsubscribeLogoutSubscription();
    this.logoutSubscription = this._logout().pipe(
      tap(_ => this.changeMobileMenuCheckbox()),
    ).subscribe();
  }

  vuLogout(): void {
    if (this.cardRefundActive) {
      return;
    }
    this.unsubscribeLogoutSubscription();
    this.logoutSubscription = this.bookingOrderService.cancelVUActiveOrder().pipe(
      switchMap(_ => this._logout()),
    ).subscribe();
  }

  logout(): void {
    this.unsubscribeLogoutSubscription();
    this.logoutSubscription = this._logout().subscribe();
  }

  _logout(): Observable<any> {
    return this.loginStateService.logout().pipe(
      tap(_ => {
        this.hidePartnerMenu();
        this.router.navigate(['/']);
      }),
    );
  }

  goToPartnerOrders(): void {
    this.partnerStateService.update(PartnerStateEnum.ShowOrders);
    this.hidePartnerMenu();
  }

  goToPartnerTransaction(): void {
    this.router.navigateByUrl('partner-code/transactions');
    this.hidePartnerMenu();
  }

  onClickPartnerMenu(): void {
    if (this.showPartnerMenu) {
      this.showPartnerMenu = false;
    } else {
      this.showPartnerMenu = true;
    }
  }

  hidePartnerMenu(): void {
    if (this.showPartnerMenu) {
      this.showPartnerMenu = false;
    }
  }

  goToDepartureTime(): void {
    this.router.navigate(['schedule']);
  }

  mobileGoToDepartureTime(): void {
    this.goToDepartureTime();
    this.changeMobileMenuCheckbox();
  }

  mobileShowPartnerTransaction(): void {
    this.changeMobileMenuCheckbox();
    this.router.navigateByUrl('partner-code/transactions');
  }

  get schedulePlanTitle(): string {
    return this.scheduleStateService.schedulePlanTitle;
  }

  get scheduleMenuLink(): string {
    return this.shopSettingsService?.shopSettings?.scheduleMenuLink || null;
  }

  get cardRefundActive(): boolean {
    return this.isVUMode &&
      this.shopSettingsService.shopSettings.allowGuestCardRefund &&
      this.refundCardStateService.isActive;
  }

  get showVULogout(): boolean {
    return this.showPartnerInformation && !this.cardRefundActive;
  }

  get partnerName(): string {
    return this.partner.name;
  }

  get isKioskMode(): boolean {
    if (environment.shopMode && ['kiosk', 'webshop&kiosk'].indexOf(environment.shopMode) !== -1) {
      return true;
    }
    return false;
  }

  public get isKiosk(): boolean {
    return this.router.url.startsWith('/kiosk');
  }
}
